import type { Router } from "@remix-run/router";
import { createBrowserRouter } from "react-router-dom";

import { OrganisationConfigManagement } from "../components/Admin/Organisations/OrganisationConfigManagementPage/OrganisationConfigManagementPage.js";
import { OrganisationManagement } from "../components/Admin/Organisations/OrganisationManagementPage/OrganisationManagement.js";
import { UserDirectoryInstanceManagementPage } from "../components/Admin/UserDirectoryInstances/UserDirectoryInstanceManagementPage/UserDirectoryInstanceManagementPage.js";
import { MHFormsLogoWithBackground } from "../components/Logo/Logo.js";
import { RootComponent } from "../components/RootComponent/RootComponent.js";
import { AdminEditReportingFieldsPage } from "../pages/AdminEditReportingFields/AdminEditReportingFieldsPage.js";
import { ArchivePage } from "../pages/ArchivePage/ArchivePage.js";
import { ArrivePlaceOfSafetyPage } from "../pages/ArrivePlaceOfSafetyPage/ArrivePlaceOfSafetyPage.js";
import { ContextSelector } from "../pages/ContextSelector/ContextSelector.js";
import { CreateIncidentWorkItemPage } from "../pages/CreateIncidentWorkItemPage/CreateIncidentWorkItemPage.js";
import { DeliberatelyThrowsAnErrorPage } from "../pages/DeliberatelyThrowsAnErrorPage/DeliberatelyThrowsAnErrorPage.js";
import { DetentionDetailsPage } from "../pages/DetentionDetailsPage/DetentionDetailsPage.js";
import { PreEndIncidentPage } from "../pages/EndIncidentPage/PreEndIncidentPage.js";
import { Error as ErrorPage } from "../pages/Error/Error.js";
import { FinishFormPage } from "../pages/FinishFormPage/FinishFormPage.js";
import { FormPdf } from "../pages/FormProgress/FormPdf.js";
import { FormProgressWrapper } from "../pages/FormProgress/FormProgress.js";
import { FormSectionCompletePage } from "../pages/FormSectionCompletePage/FormSectionCompletePage.js";
import HomePage from "../pages/Home/Home.js";
import { IncidentSummaryPage } from "../pages/IncidentSummary/IncidentSummaryPage.js";
import { InformationForHealthcarePage } from "../pages/InformationForHealthcarePage/InformationForHealthcarePage.js";
import { LeavePlaceOfSafetyPage } from "../pages/LeavePlaceOfSafetyPage/LeavePlaceOfSafetyPage.js";
import { Login } from "../pages/Login/Login.js";
import { McaAccompanimentPage } from "../pages/McaAccompanimentPage/McaAccompanimentPage.js";
import { McaCapacityAssessmentPage } from "../pages/McaCapacityAssessmentPage/McaCapacityAssessmentPage.js";
import { McaIncidentDetailsPage } from "../pages/McaIncidentDetailsPage/McaIncidentDetailsPage.js";
import { McaPersonDetailsPage } from "../pages/McaPersonDetailsPage/McaPersonDetailsPage.js";
import { PersonDetailsPage } from "../pages/PersonDetailsPage/PersonDetailsPage.js";
import { Placeholder } from "../pages/Placeholder/Placeholder.js";
import { PlacesOfSafetyPage } from "../pages/PlacesOfSafetyPage/PlacesOfSafetyPage.js";
import { PolicePresenceLogPage } from "../pages/PolicePresenceLogPage/PolicePresenceLogPage.js";
import { PolicePresenceWhatWillYouDoPage } from "../pages/PolicePresenceWhatWillYouDoPage/PolicePresenceWhatWillYouDoPage.js";
import { RecordPlaceOfSafetyPage } from "../pages/RecordPlaceOfSafetyPage/RecordPlaceOfSafetyPage.js";
import { RecordPolicePresenceRequestPage } from "../pages/RecordPolicePresenceRequestPage/RecordPolicePresenceRequestPage.js";
import { ReviewPlacesOfSafetyPage } from "../pages/ReviewPlacesOfSafetyPage/ReviewPlacesOfSafetyPage.js";
import { ShareFlow } from "../pages/Share/ShareFlow.js";
import { TriageIncidentPage } from "../pages/TriageIncidentPage/TriageIncidentPage.js";
import { UpsertPlaceOfSafetyRecordPage } from "../pages/UpsertPlaceOfSafetyRecordPage/UpsertPlaceOfSafetyRecordPage.js";
import { UserProfilePage } from "../pages/UserProfile/UserProfile.js";

/**
 * Create a React Router representing the application's routes
 *
 * @returns Router to be passed into a RouterProvider
 */
export const createRouter: () => Router = () => {
  return createBrowserRouter([
    {
      id: "root",
      path: "/",
      element: <RootComponent />,
      errorElement: <ErrorPage />,
      children: [
        {
          errorElement: <ErrorPage />,
          children: [
            {
              id: "home",
              index: true,
              element: <HomePage />,
            },
            {
              id: "admin/organisation",
              path: "/admin/organisation/:organisationId",
              element: <OrganisationManagement />,
            },
            {
              id: "admin/organisation/config",
              path: "/admin/organisation/:organisationId/config",
              element: <OrganisationConfigManagement />,
            },
            {
              id: "admin/user-directory-instance",
              path: "/admin/user-directory-instance/:userDirectoryInstanceId",
              element: <UserDirectoryInstanceManagementPage />,
            },

            {
              id: "placesOfSafety",
              path: "places-of-safety",
              element: <PlacesOfSafetyPage />,
            },
            { path: "help", element: <Placeholder title={"Help"} /> },
            {
              path: "incident/active",
              element: <HomePage />,
            },
            {
              id: "incidentWorkItem",
              path: "incident/:id",
              children: [
                {
                  path: "progress",
                  element: <FormProgressWrapper />,
                },
                {
                  path: "pdf",
                  element: <FormPdf />,
                },
                {
                  path: "triage",
                  element: <TriageIncidentPage errorHandling={"inline"} />,
                },
                {
                  path: "section-complete",
                  element: <FormSectionCompletePage />,
                },
                {
                  path: "person-details",
                  element: <PersonDetailsPage errorHandling={"inline"} />,
                },
                {
                  path: "detention-details",
                  element: <DetentionDetailsPage errorHandling={"inline"} />,
                },
                {
                  path: "information-for-healthcare",
                  element: (
                    <InformationForHealthcarePage errorHandling={"inline"} />
                  ),
                },
                {
                  path: "mca-person-details",
                  element: <McaPersonDetailsPage errorHandling={"inline"} />,
                },
                {
                  path: "mca-incident-details",
                  element: <McaIncidentDetailsPage errorHandling={"inline"} />,
                },
                {
                  path: "mca-capacity-assessment",
                  element: (
                    <McaCapacityAssessmentPage errorHandling={"inline"} />
                  ),
                },
                {
                  path: "mca-accompaniment",
                  element: <McaAccompanimentPage errorHandling={"inline"} />,
                },
                {
                  path: "record-police-presence-request/create",
                  element: <RecordPolicePresenceRequestPage />,
                },
                {
                  path: "record-police-presence-request",
                  element: <PolicePresenceLogPage />,
                },
                {
                  path: "record-police-presence-request/what-will-you-do",
                  element: <PolicePresenceWhatWillYouDoPage />,
                },
                {
                  path: "place-of-safety",
                  element: <ReviewPlacesOfSafetyPage />,
                },
                {
                  path: "place-of-safety/create-retrospective",
                  element: <UpsertPlaceOfSafetyRecordPage />,
                },
                {
                  path: "place-of-safety/:formId/edit",
                  element: <UpsertPlaceOfSafetyRecordPage edit={true} />,
                },
                {
                  path: "place-of-safety/create",
                  element: <RecordPlaceOfSafetyPage />,
                },
                {
                  path: "place-of-safety/:formId/arrival",
                  element: <ArrivePlaceOfSafetyPage />,
                },
                {
                  path: "place-of-safety/:formId/leave",
                  element: <LeavePlaceOfSafetyPage />,
                },
                {
                  path: "pre-end-incident",
                  element: <PreEndIncidentPage />,
                },
                {
                  path: "finish-form",
                  element: <FinishFormPage />,
                },
                {
                  path: "share",
                  element: <ShareFlow />,
                },
                {
                  path: "summary",
                  element: <IncidentSummaryPage />,
                },
                {
                  path: "admin/edit-reporting-fields",
                  element: <AdminEditReportingFieldsPage />,
                },
              ],
            },
            {
              path: "incident/archive",
              element: <ArchivePage />,
            },
            {
              path: "incident/create",
              element: <CreateIncidentWorkItemPage errorHandling={"throw"} />,
            },
            {
              path: "notifications",
              element: <Placeholder title={"Notifications"} />,
            },
            {
              path: "profile/:id",
              element: <UserProfilePage />,
            },
            {
              path: "test-error-page",
              element: <DeliberatelyThrowsAnErrorPage />,
            },
          ],
        },
      ],
    },
    {
      path: "login",
      element: <Login logo={<MHFormsLogoWithBackground />} />,
    },
    {
      path: "context-selector",
      element: <ContextSelector />,
    },
  ]);
};
